import {
  Application,
  Assets,
  Container,
  Culler,
  Graphics,
  Rectangle,
  Sprite,
  Texture,
  type ColorSource,
} from 'pixi.js';
import { BUILTIN_ASSETS } from './builtin-assets';
import { ELEVATION_HEIGHT, HALF_HEIGHT, HALF_WIDTH, TILE_HEIGHT, TILE_WIDTH, clamp, gridToWorld, neighbours, worldToGrid } from './iso';
import type { AssetDefinition, AssetManifest, HighlightOptions, MapDocument, MoveOptions, PlacedObject } from './types';

type PointerKind = 'start' | 'move' | 'end';
type CellHandler = (x: number, y: number, kind: PointerKind) => void;
type ObjectHandler = (id: string) => void;

interface HighlightState {
  graphic: Graphics;
  options: Required<HighlightOptions>;
  startedAt: number;
  expiresAt: number;
}

function parseColor(color: number | string | undefined, fallback = 0xffd36a): ColorSource {
  return color ?? fallback;
}

function uuid(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `ifw-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export class WorldRenderer {
  private app = new Application();
  private readonly world = new Container();
  private readonly terrainLayer = new Container();
  private readonly transitionLayer = new Container();
  private readonly shadowLayer = new Container();
  private readonly objectLayer = new Container();
  private readonly effectLayer = new Container();
  private readonly terrainTextures = new Map<string, Texture>();
  private readonly objectDisplays = new Map<string, Container>();
  private readonly shadowDisplays = new Map<string, Graphics>();
  private readonly highlights = new Map<string, HighlightState>();
  private document: MapDocument | null = null;
  private manifest: AssetManifest | null = null;
  private assetMap = new Map<string, AssetDefinition>();
  private zoom = 0.5;
  private isPanning = false;
  private pointerStart = { x: 0, y: 0, worldX: 0, worldY: 0 };
  private activePointers = new Map<number, PointerEvent>();
  private pinchDistance = 0;
  private spacePressed = false;
  private interactionMode: 'builder' | 'player' = 'player';
  private cellHandler: CellHandler | null = null;
  private objectHandler: ObjectHandler | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private animationFrame = 0;

  constructor(private readonly host: HTMLElement) {
    this.objectLayer.sortableChildren = true;
    this.shadowLayer.sortableChildren = true;
    this.effectLayer.sortableChildren = true;
    this.world.cullable = true;
    this.world.cullableChildren = true;
    this.world.addChild(this.terrainLayer, this.transitionLayer, this.shadowLayer, this.objectLayer, this.effectLayer);
  }

  async init(mode: 'builder' | 'player'): Promise<void> {
    this.interactionMode = mode;
    await this.app.init({
      width: Math.max(320, this.host.clientWidth),
      height: Math.max(320, this.host.clientHeight),
      background: '#08110d',
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoStart: false,
      preference: 'webgl',
      powerPreference: 'high-performance',
    });
    this.app.stop();
    this.app.stage.addChild(this.world);
    this.host.replaceChildren(this.app.canvas);
    this.app.canvas.className = 'ifw-canvas';
    this.bindInput();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
  }

  onCell(handler: CellHandler): void {
    this.cellHandler = handler;
  }

  onObject(handler: ObjectHandler): void {
    this.objectHandler = handler;
  }

  async setWorld(document: MapDocument, manifest: AssetManifest, fit = true): Promise<void> {
    this.document = document;
    this.manifest = manifest;
    this.assetMap = new Map([...BUILTIN_ASSETS, ...manifest.objects].map((asset) => [asset.uuid, asset]));
    await this.prepareTerrainTextures(manifest);
    await this.rebuild();
    if (fit) this.fitMap();
    this.renderNow();
  }

  async rebuild(): Promise<void> {
    if (!this.document || !this.manifest) return;
    this.destroyChildren(this.terrainLayer);
    this.destroyChildren(this.transitionLayer);
    this.destroyChildren(this.shadowLayer);
    this.destroyChildren(this.objectLayer);
    this.destroyChildren(this.effectLayer);
    this.objectDisplays.clear();
    this.shadowDisplays.clear();
    this.highlights.clear();

    const chunkSize = 16;
    for (let chunkY = 0; chunkY < Math.ceil(this.document.height / chunkSize); chunkY += 1) {
      for (let chunkX = 0; chunkX < Math.ceil(this.document.width / chunkSize); chunkX += 1) {
        const terrainChunk = new Container();
        const transitionChunk = new Container();
        terrainChunk.cullable = true;
        terrainChunk.cullableChildren = false;
        transitionChunk.cullable = true;
        transitionChunk.cullableChildren = false;
        const cliffGraphics = new Graphics();
        terrainChunk.addChild(cliffGraphics);
        this.terrainLayer.addChild(terrainChunk);
        this.transitionLayer.addChild(transitionChunk);
        const endY = Math.min(this.document.height, (chunkY + 1) * chunkSize);
        const endX = Math.min(this.document.width, (chunkX + 1) * chunkSize);
        for (let y = chunkY * chunkSize; y < endY; y += 1) {
          for (let x = chunkX * chunkSize; x < endX; x += 1) {
            this.drawCell(x, y, cliffGraphics, terrainChunk, transitionChunk);
          }
        }
      }
    }
    const objectPromises = this.document.objects.map((object) => this.drawObject(object));
    await Promise.all(objectPromises);
    this.renderNow();
  }

  updateCell(x: number, y: number): void {
    if (!this.document) return;
    void this.rebuild();
  }

  screenToCell(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.document) return null;
    const rect = this.app.canvas.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const worldX = (localX - this.world.position.x) / this.zoom;
    const worldY = (localY - this.world.position.y) / this.zoom;
    const approximate = worldToGrid(worldX, worldY);
    let best: { x: number; y: number; score: number } | null = null;
    for (let y = Math.floor(approximate.y) - 3; y <= Math.ceil(approximate.y) + 3; y += 1) {
      for (let x = Math.floor(approximate.x) - 3; x <= Math.ceil(approximate.x) + 3; x += 1) {
        if (x < 0 || y < 0 || x >= this.document.width || y >= this.document.height) continue;
        const cell = this.document.cells[y * this.document.width + x]!;
        const point = gridToWorld(x, y, cell.elevation);
        const score = Math.abs(worldX - point.x) / HALF_WIDTH + Math.abs(worldY - point.y) / HALF_HEIGHT;
        if (score <= 1.04 && (!best || point.y > gridToWorld(best.x, best.y, this.document.cells[best.y * this.document.width + best.x]!.elevation).y || score < best.score)) {
          best = { x, y, score };
        }
      }
    }
    return best ? { x: best.x, y: best.y } : null;
  }

  focusCell(cell: { x: number; y: number; z?: number }): void {
    const point = gridToWorld(cell.x, cell.y, cell.z ?? 0);
    this.world.position.set(this.app.renderer.width / 2 - point.x * this.zoom, this.app.renderer.height / 2 - point.y * this.zoom);
    this.renderNow();
  }

  fit(): void {
    this.fitMap();
    this.renderNow();
  }

  zoomBy(factor: number): void {
    const center = { x: this.app.renderer.width / 2, y: this.app.renderer.height / 2 };
    const worldCenter = { x: (center.x - this.world.x) / this.zoom, y: (center.y - this.world.y) / this.zoom };
    this.zoom = clamp(this.zoom * factor, 0.08, 2.4);
    this.world.scale.set(this.zoom);
    this.world.position.set(center.x - worldCenter.x * this.zoom, center.y - worldCenter.y * this.zoom);
    this.renderNow();
  }

  selectObject(id: string): void {
    this.highlightEntity(id, { style: 'outline', color: 0xffffff, intensity: 1 });
    this.objectHandler?.(id);
  }

  highlightEntity(id: string, options: HighlightOptions = {}): void {
    const object = this.document?.objects.find((entry) => entry.id === id);
    if (!object) return;
    this.clearHighlight(id);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const normalized: Required<HighlightOptions> = {
      style: reducedMotion && options.style === 'pulse' ? 'outline' : options.style ?? 'glow',
      color: options.color ?? 0xffd36a,
      intensity: clamp(options.intensity ?? 1, 0.1, 3),
      duration: Math.max(0, options.duration ?? 0),
    };
    const point = gridToWorld(object.x, object.y, object.z);
    const graphic = new Graphics()
      .ellipse(0, 2, HALF_WIDTH * object.footprint.width * 0.78, HALF_HEIGHT * object.footprint.height * 0.72)
      .stroke({ color: parseColor(normalized.color), width: normalized.style === 'outline' ? 5 : 9, alpha: 0.9 });
    graphic.position.set(point.x, point.y + 7);
    graphic.zIndex = point.y + 10000;
    this.effectLayer.addChild(graphic);
    const startedAt = performance.now();
    this.highlights.set(id, { graphic, options: normalized, startedAt, expiresAt: normalized.duration ? startedAt + normalized.duration : 0 });
    this.startEffectLoop();
  }

  clearHighlight(id?: string): void {
    if (id) {
      const state = this.highlights.get(id);
      state?.graphic.destroy();
      this.highlights.delete(id);
    } else {
      for (const state of this.highlights.values()) state.graphic.destroy();
      this.highlights.clear();
    }
    this.renderNow();
  }

  getEntity(id: string): PlacedObject | undefined {
    return this.document?.objects.find((entry) => entry.id === id);
  }

  getMapMetadata(): Pick<MapDocument, 'id' | 'title' | 'width' | 'height' | 'revision' | 'status'> | null {
    if (!this.document) return null;
    const { id, title, width, height, revision, status } = this.document;
    return { id, title, width, height, revision, status };
  }

  async moveEntity(id: string, path: Array<{ x: number; y: number; z?: number }>, options: MoveOptions = {}): Promise<void> {
    const object = this.getEntity(id);
    const display = this.objectDisplays.get(id);
    if (!object || !display || !path.length) throw new Error(`Unknown entity: ${id}`);
    let previous = { x: object.x, y: object.y, z: object.z };
    for (const step of path) {
      if (Math.abs(step.x - previous.x) + Math.abs(step.y - previous.y) !== 1) throw new Error('Movement paths must use adjacent grid cells.');
      if (!this.document || step.x < 0 || step.y < 0 || step.x >= this.document.width || step.y >= this.document.height) throw new Error('Movement path leaves the map.');
      await this.tweenObject(id, display, this.shadowDisplays.get(id), previous, { x: step.x, y: step.y, z: step.z ?? this.document.cells[step.y * this.document.width + step.x]!.elevation }, options);
      previous = { x: step.x, y: step.y, z: step.z ?? 0 };
      object.x = previous.x;
      object.y = previous.y;
      object.z = previous.z;
      this.dispatch('ifw:entity-step', { id, ...previous });
    }
    this.dispatch('ifw:movement-complete', { id, x: object.x, y: object.y, z: object.z });
  }

  addObject(assetId: string, x: number, y: number): PlacedObject | null {
    if (!this.document) return null;
    const asset = this.assetMap.get(assetId);
    if (!asset) return null;
    const cell = this.document.cells[y * this.document.width + x];
    if (!cell) return null;
    const object: PlacedObject = {
      id: uuid(), assetId, assetVersion: asset.version, x, y, z: cell.elevation,
      footprint: { ...asset.footprint }, state: {},
    };
    this.document.objects.push(object);
    void this.drawObject(object).then(() => this.renderNow());
    return object;
  }

  removeObjectAt(x: number, y: number): PlacedObject | null {
    if (!this.document) return null;
    const index = this.document.objects.findIndex((object) => x >= object.x && y >= object.y && x < object.x + object.footprint.width && y < object.y + object.footprint.height);
    if (index < 0) return null;
    const [removed] = this.document.objects.splice(index, 1);
    if (!removed) return null;
    this.objectDisplays.get(removed.id)?.destroy({ children: true });
    this.shadowDisplays.get(removed.id)?.destroy();
    this.objectDisplays.delete(removed.id);
    this.shadowDisplays.delete(removed.id);
    this.clearHighlight(removed.id);
    this.renderNow();
    return removed;
  }

  objectAt(x: number, y: number): PlacedObject | undefined {
    return this.document?.objects.find((object) => x >= object.x && y >= object.y && x < object.x + object.footprint.width && y < object.y + object.footprint.height);
  }

  renderNow(): void {
    if (this.app.renderer) {
      Culler.shared.cull(this.world, this.app.renderer.screen);
      this.app.render();
    }
  }

  destroy(): void {
    cancelAnimationFrame(this.animationFrame);
    this.resizeObserver?.disconnect();
    this.app.destroy(true, { children: true, texture: false, textureSource: false });
  }

  private drawCell(x: number, y: number, cliffGraphics: Graphics, terrainContainer: Container, transitionContainer: Container): void {
    if (!this.document || !this.manifest) return;
    const cell = this.document.cells[y * this.document.width + x]!;
    const point = gridToWorld(x, y, cell.elevation);
    const definition = this.manifest.terrain[cell.terrain];
    const textureKey = `${cell.terrain}:${cell.variant}`;
    const texture = this.terrainTextures.get(textureKey);
    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      sprite.position.set(point.x, point.y);
      terrainContainer.addChild(sprite);
    } else {
      const tile = new Graphics()
        .poly([0, -HALF_HEIGHT, HALF_WIDTH, 0, 0, HALF_HEIGHT, -HALF_WIDTH, 0])
        .fill({ color: definition?.color ?? '#638b45' })
        .stroke({ color: '#0c2117', alpha: 0.16, width: 1 });
      tile.position.set(point.x, point.y);
      terrainContainer.addChild(tile);
    }

    const east = x + 1 < this.document.width ? this.document.cells[y * this.document.width + x + 1] : undefined;
    const south = y + 1 < this.document.height ? this.document.cells[(y + 1) * this.document.width + x] : undefined;
    const eastLevel = east?.elevation ?? 0;
    const southLevel = south?.elevation ?? 0;
    if (cell.elevation > eastLevel) {
      const drop = (cell.elevation - eastLevel) * ELEVATION_HEIGHT;
      cliffGraphics.poly([point.x + HALF_WIDTH, point.y, point.x, point.y + HALF_HEIGHT, point.x, point.y + HALF_HEIGHT + drop, point.x + HALF_WIDTH, point.y + drop])
        .fill({ color: '#4a4037' });
    }
    if (cell.elevation > southLevel) {
      const drop = (cell.elevation - southLevel) * ELEVATION_HEIGHT;
      cliffGraphics.poly([point.x, point.y + HALF_HEIGHT, point.x - HALF_WIDTH, point.y, point.x - HALF_WIDTH, point.y + drop, point.x, point.y + HALF_HEIGHT + drop])
        .fill({ color: '#3d352f' });
    }

    const transition = new Graphics();
    transition.position.set(point.x, point.y);
    if ((cell.terrain === 'sand' || cell.terrain === 'shallow-water') && neighbours(x, y, this.document.width, this.document.height).some((adjacent) => this.document!.cells[adjacent.y * this.document!.width + adjacent.x]!.terrain.includes('water') !== cell.terrain.includes('water'))) {
      transition.poly([0, -HALF_HEIGHT + 4, HALF_WIDTH - 8, 0, 0, HALF_HEIGHT - 4, -HALF_WIDTH + 8, 0, 0, -HALF_HEIGHT + 4])
        .stroke({ color: cell.terrain.includes('water') ? '#d8f4ef' : '#f0dfad', alpha: 0.65, width: 3 });
    }
    if (cell.path) {
      const pathColor = this.manifest.paths[cell.path]?.color ?? '#a37c52';
      const connections = neighbours(x, y, this.document.width, this.document.height).filter((adjacent) => this.document!.cells[adjacent.y * this.document!.width + adjacent.x]!.path === cell.path);
      transition.circle(0, 0, 14).fill({ color: pathColor, alpha: 0.95 });
      for (const adjacent of connections) {
        const target = gridToWorld(adjacent.x, adjacent.y, this.document.cells[adjacent.y * this.document.width + adjacent.x]!.elevation);
        transition.moveTo(0, 0).lineTo((target.x - point.x) / 2, (target.y - point.y) / 2)
          .stroke({ color: pathColor, width: 26, alpha: 0.92, cap: 'round' });
      }
    }
    if (transition.context.instructions.length) transitionContainer.addChild(transition); else transition.destroy();
  }

  private async drawObject(object: PlacedObject): Promise<void> {
    const asset = this.assetMap.get(object.assetId) ?? BUILTIN_ASSETS[0]!;
    const point = gridToWorld(object.x, object.y, object.z);
    const shadow = new Graphics().ellipse(0, 0, HALF_WIDTH * object.footprint.width * asset.shadow.scale, HALF_HEIGHT * object.footprint.height * asset.shadow.scale)
      .fill({ color: '#07100b', alpha: asset.shadow.opacity });
    shadow.position.set(point.x + HALF_WIDTH * asset.shadow.offsetX, point.y + HALF_HEIGHT * asset.shadow.offsetY + 8);
    shadow.zIndex = point.y;
    this.shadowLayer.addChild(shadow);
    this.shadowDisplays.set(object.id, shadow);

    const container = new Container();
    container.position.set(point.x, point.y);
    container.zIndex = point.y + object.z * 0.01;
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', (event) => {
      event.stopPropagation();
      this.objectHandler?.(object.id);
    });
    if (asset.url) {
      try {
        const texture = await Assets.load<Texture>(asset.url);
        const sprite = new Sprite(texture);
        sprite.anchor.set(asset.pivot.x, asset.pivot.y);
        const maxWidth = HALF_WIDTH * (object.footprint.width + object.footprint.height) * 0.92;
        const maxHeight = Math.max(TILE_HEIGHT, TILE_HEIGHT * object.footprint.height * (asset.heightClass === 'landmark' ? 3.5 : asset.heightClass === 'tall' ? 2.6 : 1.8));
        const scale = Math.min(maxWidth / Math.max(1, sprite.texture.width), maxHeight / Math.max(1, sprite.texture.height));
        sprite.scale.set(scale);
        container.addChild(sprite);
      } catch {
        container.addChild(this.placeholderAsset(asset));
      }
    } else {
      container.addChild(this.placeholderAsset(asset));
    }
    this.objectLayer.addChild(container);
    this.objectDisplays.set(object.id, container);
  }

  private placeholderAsset(asset: AssetDefinition): Graphics {
    const graphic = new Graphics();
    switch (asset.type) {
      case 'vegetation':
        graphic.rect(-9, -54, 18, 55).fill('#5c3b25');
        graphic.circle(0, -72, 44).fill('#315b35');
        graphic.circle(-25, -56, 31).fill('#3f7141');
        graphic.circle(25, -57, 31).fill('#477b43');
        break;
      case 'building':
        graphic.poly([-62, -70, 0, -112, 62, -70, 0, -34]).fill(asset.heightClass === 'landmark' ? '#6c7187' : '#7c4931');
        graphic.poly([-52, -68, 0, -34, 0, 0, -52, -27]).fill('#ba9365');
        graphic.poly([0, -34, 52, -68, 52, -27, 0, 0]).fill('#8e6f51');
        if (asset.heightClass === 'landmark') graphic.rect(-17, -170, 34, 105).fill('#77796f');
        break;
      case 'character':
        graphic.circle(0, -53, 11).fill('#d8b18b');
        graphic.poly([-16, -43, 15, -43, 24, -3, -22, -3]).fill('#365e83');
        break;
      case 'creature':
      case 'vehicle':
        graphic.ellipse(0, -25, 43, 24).fill('#76533a');
        graphic.circle(32, -40, 15).fill('#76533a');
        graphic.rect(-25, -8, 8, 30).fill('#503a2b');
        graphic.rect(17, -8, 8, 30).fill('#503a2b');
        break;
      default:
        graphic.circle(0, -19, 28).stroke({ color: '#bbb5a0', width: 10 }).circle(0, -19, 11).fill('#527a8a');
    }
    return graphic;
  }

  private async prepareTerrainTextures(manifest: AssetManifest): Promise<void> {
    const jobs: Promise<void>[] = [];
    for (const [terrainId, definition] of Object.entries(manifest.terrain)) {
      definition.variants.forEach((variant, index) => {
        if (!variant.url || this.terrainTextures.has(`${terrainId}:${index}`)) return;
        jobs.push(this.diamondTexture(variant.url).then((texture) => { this.terrainTextures.set(`${terrainId}:${index}`, texture); }).catch(() => undefined));
      });
    }
    await Promise.all(jobs);
  }

  private diamondTexture(url: string): Promise<Texture> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = TILE_WIDTH;
        canvas.height = TILE_HEIGHT;
        const context = canvas.getContext('2d');
        if (!context) return reject(new Error('Canvas is unavailable.'));
        context.setTransform(HALF_WIDTH / image.width, HALF_HEIGHT / image.width, -HALF_WIDTH / image.height, HALF_HEIGHT / image.height, HALF_WIDTH, 0);
        context.drawImage(image, 0, 0);
        resolve(Texture.from(canvas));
      };
      image.onerror = () => reject(new Error(`Could not load ${url}`));
      image.src = url;
    });
  }

  private fitMap(): void {
    if (!this.document) return;
    const mapWidth = (this.document.width + this.document.height) * HALF_WIDTH;
    const mapHeight = (this.document.width + this.document.height) * HALF_HEIGHT + 4 * ELEVATION_HEIGHT;
    this.zoom = clamp(Math.min(this.app.renderer.width / mapWidth, this.app.renderer.height / mapHeight) * 0.88, 0.08, 1);
    const center = gridToWorld((this.document.width - 1) / 2, (this.document.height - 1) / 2, 0);
    this.world.scale.set(this.zoom);
    this.world.position.set(this.app.renderer.width / 2 - center.x * this.zoom, this.app.renderer.height / 2 - center.y * this.zoom);
  }

  private bindInput(): void {
    const canvas = this.app.canvas;
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    window.addEventListener('keydown', (event) => { if (event.code === 'Space') this.spacePressed = true; });
    window.addEventListener('keyup', (event) => { if (event.code === 'Space') this.spacePressed = false; });
    canvas.addEventListener('wheel', (event) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const cursor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const before = { x: (cursor.x - this.world.x) / this.zoom, y: (cursor.y - this.world.y) / this.zoom };
      this.zoom = clamp(this.zoom * Math.exp(-event.deltaY * 0.001), 0.08, 2.4);
      this.world.scale.set(this.zoom);
      this.world.position.set(cursor.x - before.x * this.zoom, cursor.y - before.y * this.zoom);
      this.renderNow();
    }, { passive: false });
    canvas.addEventListener('pointerdown', (event) => {
      canvas.setPointerCapture(event.pointerId);
      this.activePointers.set(event.pointerId, event);
      this.pointerStart = { x: event.clientX, y: event.clientY, worldX: this.world.x, worldY: this.world.y };
      this.isPanning = event.button === 1 || event.button === 2 || this.spacePressed || this.interactionMode === 'player';
      if (this.activePointers.size === 2) {
        this.isPanning = true;
        this.pinchDistance = this.pointerDistance();
      } else if (!this.isPanning) {
        const cell = this.screenToCell(event.clientX, event.clientY);
        if (cell) this.cellHandler?.(cell.x, cell.y, 'start');
      }
    });
    canvas.addEventListener('pointermove', (event) => {
      if (!this.activePointers.has(event.pointerId)) return;
      this.activePointers.set(event.pointerId, event);
      if (this.activePointers.size === 2) {
        const distance = this.pointerDistance();
        if (this.pinchDistance > 0) {
          this.zoom = clamp(this.zoom * distance / this.pinchDistance, 0.08, 2.4);
          this.world.scale.set(this.zoom);
        }
        this.pinchDistance = distance;
        const midpoint = this.pointerMidpoint();
        this.world.position.set(this.pointerStart.worldX + midpoint.x - this.pointerStart.x, this.pointerStart.worldY + midpoint.y - this.pointerStart.y);
        this.renderNow();
      } else if (this.isPanning) {
        this.world.position.set(this.pointerStart.worldX + event.clientX - this.pointerStart.x, this.pointerStart.worldY + event.clientY - this.pointerStart.y);
        this.renderNow();
      } else if (event.buttons || event.pointerType === 'touch') {
        const cell = this.screenToCell(event.clientX, event.clientY);
        if (cell) this.cellHandler?.(cell.x, cell.y, 'move');
      }
    });
    const finish = (event: PointerEvent) => {
      if (!this.isPanning && this.activePointers.size === 1) {
        const cell = this.screenToCell(event.clientX, event.clientY);
        if (cell) this.cellHandler?.(cell.x, cell.y, 'end');
      }
      this.activePointers.delete(event.pointerId);
      this.isPanning = false;
      this.pinchDistance = 0;
    };
    canvas.addEventListener('pointerup', finish);
    canvas.addEventListener('pointercancel', finish);
  }

  private resize(): void {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    this.app.renderer.resize(width, height);
    this.renderNow();
  }

  private pointerDistance(): number {
    const [first, second] = [...this.activePointers.values()];
    return first && second ? Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) : 0;
  }

  private pointerMidpoint(): { x: number; y: number } {
    const [first, second] = [...this.activePointers.values()];
    return first && second ? { x: (first.clientX + second.clientX) / 2, y: (first.clientY + second.clientY) / 2 } : { x: 0, y: 0 };
  }

  private startEffectLoop(): void {
    if (this.animationFrame) return;
    const frame = (time: number) => {
      let needsNext = false;
      for (const [id, state] of this.highlights) {
        if (state.expiresAt && time >= state.expiresAt) {
          state.graphic.destroy();
          this.highlights.delete(id);
          continue;
        }
        if (state.options.style === 'pulse' || state.options.style === 'glow' || state.options.style === 'shine') {
          state.graphic.alpha = 0.55 + Math.sin((time - state.startedAt) / 300) * 0.28;
          state.graphic.scale.set(1 + Math.sin((time - state.startedAt) / 420) * 0.06 * state.options.intensity);
          needsNext = true;
        }
        if (state.expiresAt) needsNext = true;
      }
      this.renderNow();
      if (needsNext) this.animationFrame = requestAnimationFrame(frame); else this.animationFrame = 0;
    };
    this.animationFrame = requestAnimationFrame(frame);
  }

  private tweenObject(id: string, display: Container, shadow: Graphics | undefined, from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, options: MoveOptions): Promise<void> {
    const duration = Math.max(80, options.duration ?? 420);
    const start = performance.now();
    const fromPoint = gridToWorld(from.x, from.y, from.z);
    const toPoint = gridToWorld(to.x, to.y, to.z);
    return new Promise((resolve) => {
      const frame = (time: number) => {
        let progress = clamp((time - start) / duration, 0, 1);
        if (options.easing === 'ease-in-out') progress = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        display.position.set(fromPoint.x + (toPoint.x - fromPoint.x) * progress, fromPoint.y + (toPoint.y - fromPoint.y) * progress);
        display.zIndex = display.y;
        if (shadow) {
          shadow.position.set(display.x + 10, display.y + 11);
          shadow.zIndex = display.y;
        }
        const highlight = this.highlights.get(id);
        if (highlight) {
          highlight.graphic.position.set(display.x, display.y + 7);
          highlight.graphic.zIndex = display.y + 10000;
        }
        this.renderNow();
        if (progress < 1) requestAnimationFrame(frame); else resolve();
      };
      requestAnimationFrame(frame);
    });
  }

  private dispatch(name: string, detail: Record<string, unknown>): void {
    this.host.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  }

  private destroyChildren(container: Container): void {
    const children = container.removeChildren();
    children.forEach((child) => child.destroy({ children: true }));
  }
}
